import { AuthHeaderConfigSchema } from "../authHeaderConfig/authHeaderConfigScheme";
import { ApiSchema } from "../api/apiSchema";
import { BlockPropertiesSchema } from "../apiBlocks/apiBlocks";
import { ecmSchema } from "../ecm/ecmSchema";
import { FontsSchema } from "../font/fontSchema";
import {
  LanguageSchema,
  MultilingualLanguagesSchema,
} from "../language/languageSchema";
import {
  MergeTagsArraySchema,
  SmartMergeTagsSchema,
} from "../mergeTags/mergeTagsSchema";
import { NotificationSchema } from "../notification/notificationSchema";
import {
  SavedBlocksSchema,
  SavedBlockSchema,
} from "../savedBlock/savedBlockSchema";
import { ThemeSchema } from "../theme/themeSchema";
import {
  TopolContentBlockOptionsSchema,
  TopolCustomBlocksDataSchema,
} from "../contentBlock/contentBlockScheme";
import { TopolTagAttributesSchema } from "../styleTags/styleTagsSchema";
import { HelpdeskSchema } from "../helpdesk/helpdeskSchema";
import { OptionsUserSchema } from "../user/userSchema";

import jsonSchema from "../jsonSchema";

import z from "zod";
import { DeletedItemSchema } from "@topol/image-picker";
import { OptionsPremadeBlockSchema } from "../premadeBlock/premadeBlockSchema";
import { TopolSettingsSchema } from "../template/settingsSchema";

export const DefaultTemplateLangsSchema = z
  .object({
    key: z.string().min(2),
    primary: z.boolean().catch(false),
  })
  .array();

const mutationsCallbackSchema = z.array(
  z.object({
    lang: z.string(),
    primary: z.boolean(),
  })
);

export const PluginOptionsSchema = z.object({
  authorize: z.object({
    apiKey: z.string(),
    userId: z.string().or(z.number()),
  }),
  aiLabeledElements: z.boolean().optional(),
  api: ApiSchema.optional(),
  apiAuthorizationHeader: AuthHeaderConfigSchema.or(z.string()).optional(),
  apiBlocks: z.record(z.string(), BlockPropertiesSchema).optional(),
  autosaveInterval: z.number().optional(),
  betaFeatures: z
    .object({
      carousel: z.boolean().optional(),
      loop: z.boolean().optional(),
      rating: z.boolean().optional(),
    })
    .optional(),
  chatAI: z.boolean().optional(),
  chatAIOnSegment: z.boolean().optional(),
  colors: z.array(z.string()).optional(),
  contentBlocks: TopolContentBlockOptionsSchema.optional(),
  currentUser: OptionsUserSchema.optional(),
  customBlocks: TopolCustomBlocksDataSchema.optional(),
  customFileManager: z.boolean().optional(),
  customFonts: z
    .object({
      override: z.boolean().optional(),
      fonts: FontsSchema,
    })
    .optional(),
  customLanguagePreset: z
    .object({
      langs: MultilingualLanguagesSchema,
      override: z.boolean().optional(),
    })
    .optional(),
  defaultTemplateSettings: z
    .object({
      emailWidth: z.number().optional(),
      emailBackgroundColor: z.string().optional(),
      blockBackgroundColor: z.string().optional(),
      buttonsBackgroundColor: z.string().optional(),
      hyperlinkColor: z.string().optional(),
      h1: z
        .partialRecord(TopolTagAttributesSchema, z.string().optional())
        .optional(),
      h2: z
        .partialRecord(TopolTagAttributesSchema, z.string().optional())
        .optional(),
      h3: z
        .partialRecord(TopolTagAttributesSchema, z.string().optional())
        .optional(),
      p: z
        .partialRecord(TopolTagAttributesSchema, z.string().optional())
        .optional(),
      button: TopolSettingsSchema.shape["mj-button"].optional(),
      carousel: TopolSettingsSchema.shape["mj-carousel"].optional(),
      divider: TopolSettingsSchema.shape["mj-divider"].optional(),
      gif: TopolSettingsSchema.shape["mj-gif"].optional(),
      image: TopolSettingsSchema.shape["mj-image"].optional(),
      loop: TopolSettingsSchema.shape["mj-loop"].optional(),
      social: TopolSettingsSchema.shape["mj-social"].optional(),
      text: TopolSettingsSchema.shape["mj-text"].optional(),
      column: TopolSettingsSchema.shape["mj-column"].optional(),
      section: TopolSettingsSchema.shape["mj-section"].optional(),
      "line-height": z.number().optional(),
      "structure-margin": z.string().optional(),
      "stack-columns": z.boolean().optional(),
      langs: DefaultTemplateLangsSchema.optional(),
    })
    .optional(),
  disableAiAssistant: z.boolean().optional(),
  disableAlerts: z.boolean().optional(),
  disableExtendedComparators: z.boolean().optional(),
  ecm: ecmSchema.optional(),
  emailServiceProvider: z
    .union([z.literal("mailchimp"), z.literal("sparkpost")])
    .optional(),
  enableAutosaves: z.boolean().optional(),
  enableComments: z.boolean().optional(),
  enableDarkMode: z.boolean().optional(),
  enableFileManagerInGif: z.boolean().optional(),
  enableImageToTextRatio: z.boolean().optional(),
  enableMergeTagPreview: z.boolean().optional(),
  enableSectionVariants: z.boolean().optional(),
  enableSubjectLine: z.boolean().optional(),
  enableTemplateSizeInfo: z.boolean().optional(),
  fileManagerPreferences: z
    .object({
      defaultTilesView: z.boolean().optional(),
      hidePexelsIntegration: z.boolean().optional(),
      maxUploadingFiles: z.number().int().positive().optional(),
    })
    .optional(),
  fontSizes: z.array(z.number()).optional(),
  googleApiKey: z.string().optional(),
  googlePromotionalAnnotation: z.boolean().optional(),
  helpdesk: HelpdeskSchema.optional(),
  hideSettingsTab: z.boolean().optional(),
  hideControlPanel: z.boolean().optional(),
  htmlDesktopFirst: z.boolean().optional(),
  htmlMinified: z.boolean().optional(),
  imageCompressionOptions: z
    .object({
      qualityJpeg: z.number().optional(),
      qualityPng: z.number().optional(),
      enableAutoResize: z.boolean().optional(),
      enableCompression: z.boolean().optional(),
    })
    .optional(),
  imageEditor: z.boolean().optional(),
  imageEditorOptions: z
    .object({
      hideControls: z.array(z.string()).optional(),
    })
    .optional(),
  imageEditorV2: z.boolean().optional(),
  imageMaxSize: z.number().optional(),
  lambdaStage: z
    .union([
      z.literal("local"),
      z.literal("production"),
      z.literal("stage1"),
      z.literal("stage2"),
      z.literal("stage3"),
    ])
    .optional(),
  language: LanguageSchema.optional(),
  languageMergeTag: z.string().optional(),
  light: z.boolean().optional(),
  logicalOperatorsInsideVariant: z.boolean().optional(),
  mainMenuAlign: z.union([z.literal("left"), z.literal("right")]).optional(),
  mergeTags: MergeTagsArraySchema.optional(),
  mobileFirstEnabled: z.boolean().optional(),
  multilingual: z.boolean().optional(),
  premadeBlocks: OptionsPremadeBlockSchema.optional(),
  premadeBlocksNoOverwrite: z.boolean().optional(),
  premadeTemplates: z.boolean().optional(),
  premadeTemplatesOptions: z
    .object({
      hideSearch: z.boolean().optional(),
      showDelete: z.boolean().optional(),
    })
    .optional(),
  productMergetagTags: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      })
    )
    .optional(),
  removeTopBar: z.boolean().optional(),
  renameTemplate: z.boolean().optional(),
  role: z
    .union([z.literal("manager"), z.literal("editor"), z.literal("reader")])
    .optional(),
  permissions: z
    .object({
      canLockBlocks: z.boolean().optional(),
      canLockSections: z.boolean().optional(),
      canEditLockedSections: z.boolean().optional(),
      canEditLockedBlocks: z.boolean().optional(),
      canRemoveFiles: z.boolean().optional(),
    })
    .optional(),
  savedBlocks: SavedBlocksSchema.optional(),
  showControlPanelExpand: z.boolean().optional(),
  showUnsavedDialogBeforeExit: z.boolean().optional(),
  smartMergeTags: SmartMergeTagsSchema.optional(),
  syncedSectionsEnabled: z.boolean().optional(),
  teamUsers: z.array(OptionsUserSchema).optional(),
  templateId: z.number().or(z.string()).optional(),
  testingEmails: z.array(z.string()).or(z.boolean()).optional(),
  textEditor: z.union([z.literal("tinymce"), z.literal("tiptap")]).optional(),
  textEditorToolbarBottomPosition: z
    .union([z.literal("left"), z.literal("right"), z.literal("center")])
    .optional(),
  textOverride: z.record(z.string(), z.string()).optional(),
  theme: ThemeSchema.optional(),
  tinyConfig: z.unknown().optional(),
  tiptapConfig: z
    .object({
      light: z.boolean().optional(),
      showMergeTagText: z.boolean().optional(),
    })
    .optional(),
  title: z.string().optional(),
  topBarOptions: z.array(z.string()).optional(),
  windowBar: z.array(z.string()).optional(),

  callbacks: z.object({
    onSave: z
      .function({
        input: [
          jsonSchema,
          z.any(),
          z.array(
            z.object({
              key: z.string(),
              primary: z.string(),
            })
          ),
          z.array(z.number()),
        ],
        output: z.void(),
      })
      .optional(),
    onSaveAndClose: z
      .function({
        input: [
          jsonSchema,
          z.any(),
          z.array(
            z.object({
              lang: z.string(),
              primary: z.string(),
            })
          ),
          z.array(z.number()),
        ],
        output: z.void(),
      })
      .optional(),
    onTestSend: z
      .function({
        input: [z.string(), jsonSchema, z.any()],
        output: z.void(),
      })
      .optional(),
    onBannerClick: z
      .function({ input: [jsonSchema, z.any()], output: z.void() })
      .optional(),
    onOpenFileManager: z.function({ input: [], output: z.void() }).optional(),
    onLoaded: z.function({ input: [], output: z.void() }).optional(),
    onBlockSave: z
      .function({ input: [SavedBlockSchema], output: z.void() })
      .optional(),
    onBlockRemove: z
      .function({ input: [z.number()], output: z.void() })
      .optional(),
    onBlockEdit: z
      .function({ input: [z.number()], output: z.void() })
      .optional(),
    onImageDelete: z
      .function({ input: [z.array(DeletedItemSchema)], output: z.void() })
      .optional(),
    onInit: z.function({ input: [], output: z.void() }).optional(),
    onUndoChange: z
      .function({ input: [z.number()], output: z.void() })
      .optional(),
    onRedoChange: z
      .function({ input: [z.number()], output: z.void() })
      .optional(),
    onPreview: z.function({ input: [z.any()], output: z.void() }).optional(),
    onPreviewClose: z.function({ input: [], output: z.void() }).optional(),
    onAlert: z
      .function({ input: [NotificationSchema], output: z.void() })
      .optional(),
    onClose: z.function({ input: [], output: z.void() }).optional(),
    onEdittedWithoutSaveChanged: z
      .function({ input: [z.boolean()], output: z.void() })
      .optional(),
    onOpenCustomBlockDialog: z
      .function({ input: [z.unknown()], output: z.void() })
      .optional(),
    onTemplateRename: z
      .function({ input: [z.string()], output: z.void() })
      .optional(),
    updateTestingEmailAddresses: z
      .function({ input: [z.string().array()], output: z.void() })
      .optional(),
    onError: z
      .function({
        input: [z.string(), z.string(), z.unknown().optional()],
        output: z.void(),
      })
      .optional(),
    onLanguageCreated: z
      .function({
        input: [z.string(), mutationsCallbackSchema],
        output: z.void(),
      })
      .optional(),
    onLanguageDeleted: z
      .function({
        input: [z.string(), mutationsCallbackSchema],
        output: z.void(),
      })
      .optional(),
    onLanguageSelected: z
      .function({ input: [z.string()], output: z.void() })
      .optional(),
    onPrimaryLanguageChanged: z
      .function({
        input: [z.string(), mutationsCallbackSchema],
        output: z.void(),
      })
      .optional(),
    onGetMutations: z
      .function({ input: [mutationsCallbackSchema], output: z.void() })
      .optional(),
  }),
});

export type IPluginOptions = z.infer<typeof PluginOptionsSchema>;

export type IPluginOptionsPartial = Omit<
  IPluginOptions,
  "authorize" | "callbacks"
>;

export type LambdaStage = Exclude<IPluginOptions["lambdaStage"], undefined>;
